"use client";

import Link from "next/link";
import { KpiVisual } from "@/components/landing/ModuleVisuals";
import { CheckCircle2, TrendingUp, AlertCircle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface MetricCard {
  key: string;
  titleKey: string;
  descKey: string;
  icon: "trending" | "alert" | "chart";
}

export default function KpiProductPage() {
  const { t } = useI18n();

  const metrics: MetricCard[] = [
    {
      key: "revpar",
      titleKey: "pages.product.kpi.metrics.revpar.title",
      descKey: "pages.product.kpi.metrics.revpar.desc",
      icon: "trending",
    },
    {
      key: "goppar",
      titleKey: "pages.product.kpi.metrics.goppar.title",
      descKey: "pages.product.kpi.metrics.goppar.desc",
      icon: "trending",
    },
    {
      key: "adr",
      titleKey: "pages.product.kpi.metrics.adr.title",
      descKey: "pages.product.kpi.metrics.adr.desc",
      icon: "chart",
    },
    {
      key: "occ",
      titleKey: "pages.product.kpi.metrics.occ.title",
      descKey: "pages.product.kpi.metrics.occ.desc",
      icon: "chart",
    },
    {
      key: "trevpar",
      titleKey: "pages.product.kpi.metrics.trevpar.title",
      descKey: "pages.product.kpi.metrics.trevpar.desc",
      icon: "trending",
    },
    {
      key: "cpor",
      titleKey: "pages.product.kpi.metrics.cpor.title",
      descKey: "pages.product.kpi.metrics.cpor.desc",
      icon: "chart",
    },
    {
      key: "alos",
      titleKey: "pages.product.kpi.metrics.alos.title",
      descKey: "pages.product.kpi.metrics.alos.desc",
      icon: "chart",
    },
    {
      key: "revpash",
      titleKey: "pages.product.kpi.metrics.revpash.title",
      descKey: "pages.product.kpi.metrics.revpash.desc",
      icon: "trending",
    },
    {
      key: "ddr",
      titleKey: "pages.product.kpi.metrics.ddr.title",
      descKey: "pages.product.kpi.metrics.ddr.desc",
      icon: "chart",
    },
    {
      key: "noi",
      titleKey: "pages.product.kpi.metrics.noi.title",
      descKey: "pages.product.kpi.metrics.noi.desc",
      icon: "trending",
    },
    {
      key: "rnc",
      titleKey: "pages.product.kpi.metrics.rnc.title",
      descKey: "pages.product.kpi.metrics.rnc.desc",
      icon: "chart",
    },
    {
      key: "fbppc",
      titleKey: "pages.product.kpi.metrics.fbppc.title",
      descKey: "pages.product.kpi.metrics.fbppc.desc",
      icon: "chart",
    },
  ];

  const getIconComponent = (iconType: string) => {
    switch (iconType) {
      case "trending":
        return <TrendingUp size={24} className="text-keiro-green" />;
      case "alert":
        return <AlertCircle size={24} className="text-keiro-danger" />;
      default:
        return <BarChart3 size={24} className="text-keiro-green" />;
    }
  };

  const features = [
    {
      titleKey: "pages.product.kpi.features.realtime.title",
      descKey: "pages.product.kpi.features.realtime.desc",
    },
    {
      titleKey: "pages.product.kpi.features.multiProperty.title",
      descKey: "pages.product.kpi.features.multiProperty.desc",
    },
    {
      titleKey: "pages.product.kpi.features.anomaly.title",
      descKey: "pages.product.kpi.features.anomaly.desc",
    },
    {
      titleKey: "pages.product.kpi.features.trends.title",
      descKey: "pages.product.kpi.features.trends.desc",
    },
    {
      titleKey: "pages.product.kpi.features.alerts.title",
      descKey: "pages.product.kpi.features.alerts.desc",
    },
    {
      titleKey: "pages.product.kpi.features.export.title",
      descKey: "pages.product.kpi.features.export.desc",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {t("pages.product.kpi.headline") ||
                "KPI Dashboard. 12 метрик для отеля."}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.kpi.description") ||
                `RevPAR, GOPPAR, ADR, OCC, TRevPAR, CPOR и 6 других.
                Обновляются ежедневно. Сравнивайте отели, отслеживайте тренды,
                находите аномалии автоматически.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="default" className="h-12">
                <Link href="/contact">
                  {t("pages.product.kpi.cta") || "Попробовать бесплатно"}
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12">
                <Link href="/pricing">
                  {t("pages.product.kpi.pricing") || "Смотреть тарифы"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden h-96">
            <KpiVisual />
          </div>
        </div>
      </section>

      {/* 12 Metrics Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.kpi.metricsTitle") || "12 ключевых метрик"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                {getIconComponent(metric.icon)}
                <h3 className="font-bold text-slate-900">
                  {t(metric.titleKey) || metric.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(metric.descKey) || metric.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.kpi.featuresTitle") || "Основные возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-keiro-green flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-slate-900">
                  {t(feature.titleKey) || feature.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(feature.descKey) || feature.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Real-time Updates Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.kpi.realtimeTitle") ||
                "Обновления в реальном времени"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.kpi.realtimeDesc") ||
                `PMS отправляет данные – KPI обновляются за минуты.
                RevPAR упал? Вы узнаёте сразу, а не на следующий день.
                Ежедневные snapshot'ы для анализа трендов.`}
            </p>
            <ul className="space-y-3">
              {[
                "Автосинхронизация с PMS",
                "Обновление за 5-10 минут",
                "Исторические данные за 2 года",
                "Сравнение месяц-к-месяцу и год-к-году",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-keiro-green rounded-full" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <p className="text-slate-600 text-center">
              {t("pages.product.kpi.realtimePlaceholder") ||
                "Архитектура синхронизации"}
            </p>
          </div>
        </div>
      </section>

      {/* Multi-Property Comparison */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 order-2 lg:order-1">
            <p className="text-slate-600 text-center">
              {t("pages.product.kpi.comparisonPlaceholder") ||
                "Интерактивное сравнение отелей"}
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.kpi.comparisonTitle") ||
                "Сравнивайте отели в цепи"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.kpi.comparisonDesc") ||
                `Если у вас 5+ отелей, видите KPI каждого в одной таблице.
                Кто лидирует по RevPAR? Какой отель теряет гостей?
                Быстро выявляйте разницу в производительности.`}
            </p>
            <ul className="space-y-3">
              {[
                "Топ и аутсайдеры по метрике",
                "Стандартные метрики для сравнения",
                "Расчёт разницы в %",
                "Экспорт в Excel по отелям",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-keiro-green rounded-full" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Anomaly Detection */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.kpi.anomalyTitle") ||
            "Автоматическое выявление аномалий"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.kpi.anomaly.sharp.title") || "Резкий скачок",
              desc: t("pages.product.kpi.anomaly.sharp.desc") || "Внезапное падение RevPAR на 20%+ от плана",
              emoji: "📉",
            },
            {
              title: t("pages.product.kpi.anomaly.trend.title") || "Негативный тренд",
              desc: t("pages.product.kpi.anomaly.trend.desc") || "RevPASH снижается 3-5 дней подряд",
              emoji: "📊",
            },
            {
              title: t("pages.product.kpi.anomaly.budget.title") || "Превышение плана",
              desc: t("pages.product.kpi.anomaly.budget.desc") || "Расходы прыгают выше бюджета",
              emoji: "⚠️",
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-keiro-danger/20 rounded-xl p-6 bg-keiro-danger/5">
              <div className="text-3xl mb-3">{item.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.kpi.useCasesTitle") || "Типовые сценарии"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: t("pages.product.kpi.useCase1.title") || "GM / Operation Manager",
              desc: t("pages.product.kpi.useCase1.desc") || "Ежедневный мониторинг RevPAR и OCC. Видеть слабые дни заранее.",
              emoji: "👔",
            },
            {
              title: t("pages.product.kpi.useCase2.title") || "Revenue Manager",
              desc: t("pages.product.kpi.useCase2.desc") || "TRevPAR, ADR, OCC в одном месте. Быстро реагировать на спрос.",
              emoji: "📈",
            },
            {
              title: t("pages.product.kpi.useCase3.title") || "CFO / Собственник",
              desc: t("pages.product.kpi.useCase3.desc") || "GOPPAR и NOI по отелям. Понять рентабельность.",
              emoji: "💰",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{useCase.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-3">{useCase.title}</h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.kpi.ctaTitle") ||
              "Готовы к реальным KPI?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.kpi.ctaDesc") ||
              "14 дней бесплатного доступа, без кредитной карты"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.kpi.ctaButton") || "Начать бесплатно"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
